import { useMutation } from '@tanstack/react-query'
import { SubmitHandler, useFormContext } from 'react-hook-form'
import { useCallback, useState } from 'react'
import { Link } from 'react-router'
import APIService from 'api/APIService'
import { MotusAuthFormValue } from 'pages/auth/type'
import { ErrorMessage, UserProfile } from 'types/entity'
import { useUserProfile } from 'core/hooks/data/useDataHook'
import { ERROR_PASSWORD_NOT_JUST_WHITESPACE } from 'pages/account/profile/validate'
import Button from 'core/components/buttons/Button'
import SmartField from 'core/components/form/SmartField'
import Input from 'core/components/inputs/Input'
import useAuth from 'core/hooks/auth/useAuth'
import { SendPasswordResetUrl, StartUrl } from 'constants/RouteUrls'
import useBlueBackground from 'pages/sign-up/useBlueBackground'
import CardPageWrapper from 'core/components/page-wrapper/CardPageWrapper'
import useNotifications from 'core/hooks/ui/useNotifications'
import PurpleAccessModal from 'pages/auth/purple/PurpleAccessModal'
import useModal from 'core/hooks/ui/useModal'
import useLogin from 'core/hooks/auth/useLogin'
import {
  getFutureAccessDate,
  hasMileMarkerAccess
} from 'pages/auth/purple/helper'
import MotusIcon from 'core/components/motus-icons/MotusIcons'
import { getErrorMessage } from 'utils/AppHelper'

const PurpleLoginPage = () => {
  useBlueBackground()
  const { control, handleSubmit, getValues } =
    useFormContext<MotusAuthFormValue>()
  const { setData } = useUserProfile()
  const { setMotusTokens } = useAuth()
  const { handleMotusLogin } = useLogin()
  const notification = useNotifications()
  const { open, openModal, closeModal } = useModal()
  const [accessDate, setAccessDate] = useState<null | string>(null)

  const [focusPassword] = useState<boolean>(!!getValues('username'))
  const [showPassword, setShowPassword] = useState(false)

  const {
    isPending: isLoading,
    isError,
    error,
    mutate
  } = useMutation({
    mutationKey: ['motus', 'login'],
    mutationFn: async (credentials: { loginId: string; password: string }) => {
      const refresh_token = await APIService.motus.getRefreshToken(credentials)

      const access_token = await APIService.motus.getAccessToken({
        refreshToken: refresh_token
      })

      const settings = await APIService.motus.getUserSettings({
        accessToken: access_token
      })

      const user = await APIService.motus.getCurrentUser({
        accessToken: access_token
      })

      const hasAccess = hasMileMarkerAccess(settings)
      const futureAccessDate = getFutureAccessDate(settings)
      const isConverted =
        !!settings.convertedToNewApp || !!user?.teams?.[0]?.converted_to_new_app

      return {
        hasAccess,
        futureAccessDate,
        refresh_token,
        access_token,
        user,
        isConverted
      }
    },
    onSuccess: ({
      hasAccess,
      futureAccessDate,
      refresh_token,
      access_token,
      user,
      isConverted
    }: {
      hasAccess: boolean
      futureAccessDate: string | null
      refresh_token: string
      access_token: string
      user: UserProfile
      isConverted: boolean
    }) => {
      if (hasAccess && user) {
        // Set motus user data
        setMotusTokens({
          refreshToken: refresh_token,
          accessToken: access_token
        })
        setData(user)

        handleMotusLogin({
          isConverted,
          user
        })
        return
      }

      if (!hasAccess) {
        setAccessDate(futureAccessDate)
        openModal()
      }
    },
    onError: () => {
      notification.error({
        title: 'Login error',
        subtitle: 'Could not login. Please try again.'
      })
    }
  })

  const onSubmit: SubmitHandler<MotusAuthFormValue> = useCallback(
    ({ username, password }) => {
      notification.dismissAll()
      mutate({
        loginId: username,
        password
      })
    },
    [mutate, notification]
  )

  return (
    <CardPageWrapper
      backUrl={StartUrl}
      company="motus"
      dataTitle="login-screen"
    >
      <PurpleAccessModal
        show={open}
        onClose={closeModal}
        accessDate={accessDate}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-10 mt-6">
          <h3 className="text-4xl mb-4 text-primary font-bold">Sign In</h3>
          <p className="text-lg">
            Complete the text field with the credentials from your invite.
          </p>
        </div>

        <div className="mt-6 flex flex-col gap-2">
          <SmartField
            controllerProps={{
              control,
              name: 'username',
              rules: {
                required: 'Username is required',
                validate: {
                  noBlank: (value) =>
                    value.trim().length > 0 ||
                    'Username cannot be just whitespace'
                }
              }
            }}
            label="Username"
            orientation="vertical"
            requirementDecoration={null}
          >
            <Input
              type="text"
              autoFocus={!focusPassword}
              placeholder="Username"
              className="text-lg mb-2 w-full py-3.5"
            />
          </SmartField>

          <SmartField
            controllerProps={{
              control,
              name: 'password',
              rules: {
                required: 'Password is required',
                validate: {
                  noBlank: (value) =>
                    value.trim().length > 0 ||
                    ERROR_PASSWORD_NOT_JUST_WHITESPACE
                }
              }
            }}
            label="Password"
            orientation="vertical"
            requirementDecoration={null}
          >
            <Input
              type={showPassword ? 'text' : 'password'}
              autoFocus={focusPassword}
              placeholder="Enter your password"
              className="text-lg mb-2 w-full py-2.5"
              suffix={
                <MotusIcon
                  name={showPassword ? 'eye-slash-outline' : 'eye-open-outline'}
                  className="cursor-pointer"
                  onClick={() => {
                    setShowPassword(!showPassword)
                  }}
                />
              }
            />
          </SmartField>
          <Link
            to={SendPasswordResetUrl}
            className="select-none text-primary! underline"
          >
            Forgot Password?
          </Link>

          <Button
            type="submit"
            variant="primary"
            status={isLoading ? 'loading' : 'idle'}
            disabled={isLoading}
            title="submit-button"
            className="w-full mt-2"
          >
            Sign In
          </Button>
        </div>

        {isError && (
          <div>
            <p className="pt-2 text-danger">
              {getErrorMessage(
                error as ErrorMessage,
                'An unknown error occurred'
              )}
            </p>
          </div>
        )}
      </form>
    </CardPageWrapper>
  )
}

export default PurpleLoginPage
